"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  Sparkles,
  Search,
  Bell,
  Settings,
  User,
  ChevronDown,
  Building2,
  HelpCircle,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { UserSettingsDialog } from "./user-settings-dialog";
import { TenantSwitchDialog } from "./tenant-switch-dialog";
import { useAuth } from "@/contexts/auth-context";

interface TopBarProps {
  onToggleSidebar?: () => void;
  sidebarCollapsed?: boolean;
  onLogout?: () => void;
}

export function TopBar({ onToggleSidebar: _onToggleSidebar, sidebarCollapsed: _sidebarCollapsed, onLogout }: TopBarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, tenant } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showTenantSwitch, setShowTenantSwitch] = useState(false);

  // Get current page title based on pathname
  const getPageTitle = () => {
    if (pathname === "/") return "平台总览";
    if (pathname.startsWith("/P/governance")) return "平台治理";
    if (pathname.startsWith("/P/auth")) return "权限中心";
    if (pathname.startsWith("/P/audit")) return "安全审计";
    if (pathname.startsWith("/P/docs")) return "文档管理";
    if (pathname.startsWith("/A")) return "瑶光AI";
    if (pathname.startsWith("/P/mail")) return "灵鹊";
    if (pathname.startsWith("/B")) return "中庭·业务台";
    if (pathname.startsWith("/E/")) return "开放平台·灵钛";
    if (pathname.startsWith("/F/")) return "租户面·灵耀";
    if (pathname.startsWith("/G/")) return "工作面·灵犀";
    if (pathname.startsWith("/M/")) return "移动面·灵移";
    if (pathname.startsWith("/L/")) return "数据面·灵泉";
    return "天枢超级底座";
  };

  return (
    <header className="h-14 border-b border-border/50 bg-white/95 backdrop-blur-sm flex items-center px-4 shrink-0 z-50">
      {/* Left: Logo */}
      <div className="flex items-center gap-2.5">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5">
          <div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-stellar-gold/20 to-stellar-gold/5 border border-stellar-gold/30">
            <Sparkles className="w-4 h-4 text-stellar-gold" />
            <div className="absolute inset-0 rounded-lg bg-stellar-gold/10 animate-pulse-glow" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gradient-gold tracking-wide leading-tight">
              天枢 · 超级底座
            </span>
            <span className="text-[9px] text-muted-foreground tracking-wider leading-tight">
              TIANSHU SUPER BASE
            </span>
          </div>
        </Link>

        {/* Divider */}
        <div className="w-px h-6 bg-border/50 mx-2" />

        {/* Current Page */}
        <span className="text-sm text-foreground/80 font-medium">
          {getPageTitle()}
        </span>
      </div>

      {/* Center: Search */}
      <div className="flex-1 flex justify-center px-8">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="搜索功能、租户、应用..."
            className="w-full h-9 pl-9 pr-4 rounded-lg bg-gray-50 border border-transparent focus:border-stellar-gold/30 focus:bg-white focus:outline-none text-sm transition-all placeholder:text-muted-foreground/60"
          />
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-1">
        {/* Notifications */}
        <button className="relative p-2 rounded-lg hover:bg-gray-100 text-muted-foreground hover:text-foreground transition-colors">
          <Bell className="w-4.5 h-4.5" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
        </button>

        {/* Settings */}
        <button className="p-2 rounded-lg hover:bg-gray-100 text-muted-foreground hover:text-foreground transition-colors">
          <Settings className="w-4.5 h-4.5" />
        </button>

        {/* Divider */}
        <div className="w-px h-6 bg-border/50 mx-1" />

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-stellar-gold/30 to-stellar-gold/10 border border-stellar-gold/20 flex items-center justify-center">
              <User className="w-3.5 h-3.5 text-stellar-gold" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-xs font-medium text-foreground leading-tight">
                {user?.displayName || "管理员"}
              </span>
              <span className="text-[10px] text-muted-foreground leading-tight">
                {tenant?.name || "系统管理"}
              </span>
            </div>
            <ChevronDown className={cn(
              "w-3.5 h-3.5 text-muted-foreground transition-transform",
              showUserMenu && "rotate-180"
            )} />
          </button>

          {/* User Dropdown */}
          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-border/50 py-1 z-50">
                <div className="px-3 py-2 border-b border-border/30">
                  <p className="text-sm font-medium">{user?.displayName || "管理员"}</p>
                  <p className="text-xs text-muted-foreground">{user?.email || "admin@huawei.com"}</p>
                </div>
                <button
                  onClick={() => { setShowSettings(true); setShowUserMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                >
                  个人设置
                </button>
                <button
                  onClick={() => { setShowTenantSwitch(true); setShowUserMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                >
                  切换租户
                </button>
                <button className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors">
                  帮助文档
                </button>
                <div className="border-t border-border/30 my-1" />
                <button
                  onClick={() => { onLogout?.(); router.push("/login"); }}
                  className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  退出登录
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <UserSettingsDialog open={showSettings} onClose={() => setShowSettings(false)} />
      <TenantSwitchDialog open={showTenantSwitch} onClose={() => setShowTenantSwitch(false)} />
    </header>
  );
}
